"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ThemeToggle } from "@/components/admin/AdminTopBar/partials/ThemeToggle";
import { AccessibilityToggle } from "@/components/accessibility/AccessibilityToggle/AccessibilityToggle";
import type { ThemeMode } from "@/lib/theme/types";
import { WindowArea } from "@/components/layouts/WindowArea/WindowArea";
import { useAdmin } from "@/lib/admin/context";
import { extractLocaleFromPath } from "@/lib/i18n/types";
import type { CmsConfig, CmsConfigMeta } from "@/lib/api/cms-config";
import styles from "./HDefault.module.css";

/**
 * HDefault — domyślny header strony publicznej.
 *
 * Po unifikacji visit page widoczny IDENTYCZNIE dla admina i visitor'a.
 *
 * Layout (dwa wiersze):
 *   - Wiersz 1 (top row):
 *     - Lewa strona: logo (z `cmsConfig.logo_url` lub fallback `/imperial-logo.svg`)
 *       **opakowane w `<Link href="/">`** — klik logo ZAWSZE prowadzi do strony głównej
 *     - Środek-prawa: 4 kolumny okien (`headerCol1..4`) wyrównane do prawej,
 *       kontener zajmuje max 50% szerokości nagłówka. Admin wrzuca tam menu,
 *       search, language switcher itp.
 *     - Prawa strona: ThemeToggle (motyw light/dark/hc) — user preference
 *       dla wyglądu strony
 *   - Wiersz 2 (bottom row): pojedynczy area `headerBottom` rozciągnięty
 *     na pełną szerokość headera (pod logo + 4 kolumnami + ThemeToggle).
 *     Typowe zastosowania: menu główne, breadcrumbs, search bar.
 *
 * Logo source priority:
 *   1. `cmsConfig.logo_url` jeśli ustawione (może być pełen URL z dowolnej
 *      domeny — np. CDN klienta, lub relative path)
 *   2. Fallback `/imperial-logo.svg` — kopia z `web/ADMIN/template/default/gfx/svgs/logo.svg`,
 *      trzymana w `public/` żeby NIE pobierać cross-domain
 *
 * Uwaga: używamy natywnego `<img>` zamiast `next/image` ponieważ:
 *   - Logo może mieć dowolny format (SVG/PNG/JPEG/WebP) i dowolny aspect ratio
 *   - SVG i tak wymaga `unoptimized`, więc Next image optimization nie daje korzyści
 *   - Hardcoded `width`/`height` w `next/image` nie pasuje do dynamic logo
 *   - Native `<img>` z CSS `max-height` + `object-fit: contain` jest elastyczne
 *
 * Konwencja "Header / Footer jako sub-layouts" — zob. `.claude/CLAUDE.md`.
 */
export function HDefault({ cmsConfig, cmsConfigMeta, fallbackBrand }: HDefaultProps) {
  const logoSrc = cmsConfig?.logo_url || "/imperial-logo.svg";
  const logoAlt = cmsConfig?.logo_alt || fallbackBrand;

  /* Logo link prowadzi do homepage w aktualnym locale — `/pl` z `/pl/o-nas`,
     `/en` z `/en/about-us` itp. NIE do globalnego `/` (które redirect'uje
     na DEFAULT_LOCALE — niepotrzebny round-trip). */
  const pathname = usePathname() ?? "/";
  const locale = extractLocaleFromPath(pathname);

  /* Drugi wiersz nagłówka — pomijamy CAŁY wrapper (nie tylko WindowArea) gdy
     `headerBottom` jest disabled w bieżącym scope. Bez tego sam div .bottomRow
     z border-top + padding + min-height robił widoczny pas mimo wyłączonego
     area. WindowArea i tak zwraca null przy disabled — sprawdzamy tutaj żeby
     dodatkowo zwinąć kontener. */
  const { disabledAreas } = useAdmin();
  const headerBottomDisabled = (disabledAreas as readonly string[]).includes("headerBottom");

  /* Dostępne motywy = light (zawsze) + ext-gated toggle'e admina. ThemeToggle
     pokazuje przełącznik tylko gdy lista ma >1 element. Mapowanie:
     - `enable_dark_version`     -> `dark`
     - `enable_funeral_version`  -> `funeral` (grayscale filter, template-agnostic)
     - `enable_contrast_version` -> `hc` (high contrast WCAG AAA) */
  const availableThemes: ThemeMode[] = ["light"];
  if (cmsConfig?.enable_dark_version === "1") availableThemes.push("dark");
  if (cmsConfig?.enable_funeral_version === "1") availableThemes.push("funeral");
  if (cmsConfig?.enable_contrast_version === "1") availableThemes.push("hc");

  return (
    <header className={styles.root}>
      <div className={styles.inner}>
      <div className={styles.topRow}>
        <div className={styles.brand}>
          <Link href={`/${locale}`} className={styles.brandLink} aria-label="Strona główna">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={logoSrc}
              alt={logoAlt}
              className={styles.logo}
              width={200}
              height={60}
            />
          </Link>
        </div>

        {/* Windows areas — 4 kolumny wyrównane do prawej, kontener max 50%
            nagłówka. Admin wrzuca tu okna (menu, search, language switcher)
            przez `LayoutAndWindowsPanel`. WindowArea renderuje ten sam visitor
            view dla admina i gościa. */}
        <div className={styles.windowsArea} aria-label="Kolumny nagłówka">
          <div className={styles.windowsAreaColumn}>
            <WindowArea name="headerCol1" label="Kolumna 1 nagłówka" />
          </div>
          <div className={styles.windowsAreaColumn}>
            <WindowArea name="headerCol2" label="Kolumna 2 nagłówka" />
          </div>
          <div className={styles.windowsAreaColumn}>
            <WindowArea name="headerCol3" label="Kolumna 3 nagłówka" />
          </div>
          <div className={styles.windowsAreaColumn}>
            <WindowArea name="headerCol4" label="Kolumna 4 nagłówka" />
          </div>
        </div>

        <div className={styles.controls}>
          <ThemeToggle availableThemes={availableThemes} />
          {/* WCAG — ikona panelu dostępności dla odwiedzającego. Gated by
              rozszerzenie `cms_wcag_availability` (`meta.wcag_available`).
              Klik otwiera panel z prawej (rozmiar czcionki + kontrast +
              podświetlanie linków + skróty klawiszowe Alt+1/2). */}
          {cmsConfigMeta?.wcag_available && <AccessibilityToggle />}
        </div>
      </div>

      {/* Wiersz 2 — full-width area `headerBottom` pod logo + kolumnami + theme
          toggle. Admin wrzuca okna typu menu główne / breadcrumbs / search bar.
          Pomijamy ENTIRE wrapper gdy area disabled — żeby nie zostawiać widocznego
          paska z border-top + padding + min-height. */}
      {!headerBottomDisabled && (
        <div className={styles.bottomRow}>
          <WindowArea name="headerBottom" label="Dolny pas nagłówka" />
        </div>
      )}
      </div>
    </header>
  );
}

export type HDefaultProps = {
  cmsConfig: CmsConfig | null;
  /** Meta z `cmsConfig` response — używamy `wcag_available` do gating'a
   *  `<AccessibilityToggle />`. Opcjonalny dla backward-compat z miejscami
   *  które jeszcze nie przekazują meta. */
  cmsConfigMeta?: CmsConfigMeta | null;
  /** Wyświetlany jako `alt` dla logo gdy `cmsConfig.logo_alt` nie jest ustawiony. */
  fallbackBrand: string;
};
